import { Container, Alert, Button, Card, Col, Row } from 'react-bootstrap';
import HTMLRenderer from '../HTMLRenderer';
import { addHttpsToUrl, ensureHttpsInHrefs } from 'libs/string';
import currencyOptions from '../Constant/currencyOptions';
import { useLocation } from 'react-router-dom';
import {
  searchProgramsResultsModalStateSignal,
  searchProgramsResultsSelectedProgram,
} from '../SearchProgramResults/SearchProgramsResults.signal';
import fundingProgramDetailSignal from './FundingProgramDetail.signal';
import { useEffect } from 'react';
import { fetchAndSetProgramDetail } from './FundingProgramDetail.helpers';
import ProgramRequestSubmissionModal from './components/LenderProgramInformationAndAccessModal';
import userAccountSignal from 'signals/UserAccount.signal';
import $appSettings from 'signals/AppSettings.signal';

function eligibilityString(eligibility) {
  if (Array.isArray(eligibility)) {
    return eligibility.map((e) => ensureHttpsInHrefs(e));
  }

  if (typeof eligibility === 'string') {
    return ensureHttpsInHrefs(eligibility);
  }

  return '';
}

const FundingProgramDetail = () => {
  const { pathname } = useLocation();
  const { PORTAL_TYPES } = $appSettings.value.constants;
  const { fundingProgram, programRequests, fundingProgramMemberships } =
    fundingProgramDetailSignal.value;
  const submittedRequest = programRequests.find(
    (programRequest) => programRequest.fundingProgramId === fundingProgram.id
  );
  const existingFundingProgramMembership = fundingProgramMemberships.find(
    (fpm) => fpm.fundingProgramId === fundingProgram.id
  );

  const fundingProgramId = pathname.split('/')[pathname.split('/').length - 1];

  useEffect(() => {
    fetchAndSetProgramDetail(fundingProgramId);
  }, [fundingProgramId]);

  if (!fundingProgram) {
    return null;
  }

  const backgroundStyle = fundingProgram.headerImageUrl 
    ? { 
        backgroundImage: `url(${fundingProgram.headerImageUrl})`,
      } 
    : { 
        backgroundColor: '#6c757d', 
      };

  return (
    <>
      <ProgramRequestSubmissionModal />
      <Card className="rounded-0 mb-32">
        <div 
          className="hero-wrapper position-relative w-100 d-flex align-items-center"
          style={{
            ...backgroundStyle,
            height: '400px',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div 
            className="position-absolute w-100 h-100" 
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.25)',
              backdropFilter: `blur(3px)`,
            }}
          />
          
          <Container className="position-relative z-index-1 text-center text-white px-3">
            <h1 
              className="display-3 mb-4"
              style={{ textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)' }}
            >{fundingProgram.name}</h1>
          </Container>
        </div>
        <Card.Body>
          {(submittedRequest || existingFundingProgramMembership) && (
            <Alert variant="success">
              <Alert.Heading>
                {fundingProgram.name} -{' '}
                {fundingProgram.referenceProgram.programName}
              </Alert.Heading>
              {existingFundingProgramMembership ? (
                <p>You are already a part of this program.</p>
              ) : (
                <p>
                  Program access has been requested. You may monitor your status
                  for this request in your Programs tab through the main
                  dashboard.
                </p>
              )}
            </Alert>
          )}
          <p className="font-weight-bold mb-8">Overview</p>
          <Col className="px-16 py-8">
            <HTMLRenderer
              className="mb-0"
              htmlString={ensureHttpsInHrefs(fundingProgram.overview)}
            />
          </Col>
          <p className="font-weight-bold my-8">Eligibility</p>
          <Col className="px-16 py-8">
            <HTMLRenderer
              className="mb-0"
              htmlString={eligibilityString(fundingProgram.eligibility)}
            />
          </Col>
          <p className="font-weight-bold my-8">Ineligible Businesses</p>
          <Col className="px-16 py-8">
            <HTMLRenderer
              className="mb-0"
              htmlString={ensureHttpsInHrefs(
                fundingProgram.ineligibleBusinesses
              )}
            />
          </Col>
          <p className="font-weight-bold my-8">Program Rules</p>
          <Col className="px-16 py-8">
            <HTMLRenderer
              className="mb-0"
              htmlString={ensureHttpsInHrefs(fundingProgram.programRules)}
            />
          </Col>
          <Row>
            <Col>
              <p className="font-weight-bold my-8">Program Funds</p>
              <Col className="px-16 py-8">
                <p className="mb-0">
                  {(
                    fundingProgram.ceiling -
                    fundingProgram.fundsAlreadyAllocated
                  ).toLocaleString('en-US', currencyOptions.currencyUS)}
                </p>
              </Col>
            </Col>
            <Col>
              <p className="font-weight-bold my-8">Website</p>
              {fundingProgram.website ? (
                <Col className="px-16 py-8">
                  <a
                    className="mb-0"
                    target="_blank"
                    href={addHttpsToUrl(fundingProgram.website)}
                    rel="noreferrer"
                  >
                    {fundingProgram.website}
                  </a>
                </Col>
              ) : (
                <Col className="px-16 py-8">
                  <p className="mb-0">-</p>
                </Col>
              )}
              <p className="font-weight-bold my-8">Remaining funds</p>
              <Col className="px-16 py-8">
                <p className="mb-0">
                  {fundingProgram.stats.remaining.toLocaleString('en-US', currencyOptions.currencyUS)}
                </p>
              </Col>
            </Col>
          </Row>

          {userAccountSignal.value.userData.account.portalType !==
            PORTAL_TYPES.business && (
            <Row className="mt-8 px-16 justify-content-end">
              {!submittedRequest && !existingFundingProgramMembership && (
                <Button
                  className="rounded"
                  onClick={() => {
                    searchProgramsResultsSelectedProgram.update(fundingProgram);

                    if (
                      userAccountSignal.value.userData.account.portalType ===
                      PORTAL_TYPES.vc
                    ) {
                      searchProgramsResultsModalStateSignal.update({
                        step: 1,
                        show: true,
                      });
                    } else {
                      searchProgramsResultsModalStateSignal.update({
                        step: 2,
                        show: true,
                      });
                    }
                  }}
                >
                  Request Approval
                </Button>
              )}
            </Row>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default FundingProgramDetail;
